import React, {useRef, useState} from 'react';
import {submitContact, ContactFormData, RegistrationFormData, submitRegistration} from '../services/api';
import styled from 'styled-components';
import ReCAPTCHA from "react-google-recaptcha";
import {colors} from "../styles/GlobalStyles";

// Styled Components based on RegistrationForm.tsx and Style Guide
const PageWrapper = styled.div`
  padding: 20px;
  font-family: 'Oswald', 'Arial Black', sans-serif; /* From Style Guide */
  color: #133B5C; /* Navy Blue from Style Guide */
  max-width: 700px; /* Increased max-width for better form layout */
  margin: 40px auto; /* Centering the page content with more top/bottom margin */
  /* background-color: #F9F6F1; Removed to let page background show through */
  /* border-radius: 8px; Removed for no card appearance */
  /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); Removed for no card appearance */
`;

const FormTitle = styled.h1`
  text-align: center;
  margin-bottom: 30px;
  color: #133B5C; /* Navy Blue */
  text-transform: uppercase; /* Style Guide: Oswald, All Caps */
  font-weight: bold; /* Style Guide: Oswald, Bold */
`;

const FormWrapper = styled.form`
  padding: 30px 40px;
  background-color: #FFFFFF; /* White background for the form itself */
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 0.9em;
  color: #133B5C; /* Navy Blue */
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #999589; /* Warm Gray for borders */
  border-radius: 4px;
  font-size: 1em;
  font-family: Arial, sans-serif; /* Fallback font for inputs */
  box-sizing: border-box; /* Ensure padding doesn't affect width */

  &:focus {
    outline: none;
    border-color: #2A7131; /* Forest Green on focus */
    box-shadow: 0 0 0 2px rgba(42, 113, 49, 0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #999589; /* Warm Gray for borders */
  border-radius: 4px;
  font-size: 1em;
  font-family: Arial, sans-serif; /* Fallback font for inputs */
  min-height: 100px;
  box-sizing: border-box; /* Ensure padding doesn't affect width */
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2A7131; /* Forest Green on focus */
    box-shadow: 0 0 0 2px rgba(42, 113, 49, 0.2);
  }
`;

const SubmitButton = styled.button`
  background-color: #133B5C; /* Navy Blue */
  color: #F9F6F1; /* Off-White text */
  border: none;
  padding: 12px 25px;
  font-size: 1.1em;
  font-family: 'Oswald', sans-serif;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: block;
  width: 100%;
  margin-top: 15px;

  &:hover:not(:disabled) {
    background-color: #2A7131; /* Forest Green on hover */
  }

  &:disabled {
    background-color: #999589; /* Warm Gray when disabled */
    cursor: not-allowed;
  }
`;

const FooterText = styled.p`
  text-align: center;
  margin-top: 30px;
  font-size: 1.1em; /* Increased from 0.9em */
  color: #133B5C; /* Navy Blue */
  line-height: 1.6;
`;


const RecaptchaWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
`;

const StatusMessage = styled.p<{ success?: boolean }>`
  text-align: center;
  margin-top: 15px;
  padding: 10px;
  border-radius: 4px;
  font-weight: bold;
  color: ${props => (props.success ? colors.forestGreen : '#D8000C')}; /* Red for error */
  background-color: ${props => (props.success ? '#DFF2BF' : '#FFD2D2')}; /* Light green/red background */
`;



const ContactUsPage: React.FC = () => {

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Google's Test Key
  const RECAPTCHA_SITE_KEY = '6LfjIkArAAAAACIy4Si-RbG2bdPsxohydg8ACvLv';

  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    phoneNumber: '',
    message: '',
    action: 'submitContact',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setSubmitStatus(null); // Clear status on recaptcha change
  };

  const handleSubmit =  async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // TODO: Implement form submission logic (e.g., send data to an API)
    //console.log('Form data submitted:', formData);
    //alert('Thank you for your message! We will get back to you soon.');
    // Reset form after submission (optional)


    try {
      const result = await submitContact(formData, recaptchaToken);
      setIsSubmitting(true);
      setSubmitStatus(result);

      if (result.success) {
        // Optionally reset form on success
        setFormData({
          firstName: '',
          lastName: '',
          businessName: '',
          email: '',
          phoneNumber: '',
          message: '',
          action: 'submitContact'
        });
        recaptchaRef.current?.reset(); // Reset reCAPTCHA
        setRecaptchaToken(null);
      }
    } catch (error) {
      console.error('Form submission error:', error);
       setSubmitStatus({ success: false, message: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }


  };

  return (
    <PageWrapper>
      <FormTitle>Contact Us</FormTitle>
      <FormWrapper onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="firstName">First Name:</Label>
          <Input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="lastName">Last Name:</Label>
          <Input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="businessName">Business Name:</Label>
          <Input
            type="text"
            id="businessName"
            name="businessName"
            value={formData.businessName}
            onChange={handleInputChange}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">Email:</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="phoneNumber">Phone Number:</Label>
          <Input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="message">Message:</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <RecaptchaWrapper>
          <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
          />
        </RecaptchaWrapper>

        {submitStatus && (
            <StatusMessage success={submitStatus.success}>
              {submitStatus.message}
            </StatusMessage>
        )}

        <SubmitButton type="submit" disabled={isSubmitting || !recaptchaToken }>{isSubmitting ? 'Sending...' : 'Send Message'}</SubmitButton>
      </FormWrapper>
      <FooterText>
        Contact us by phone: Thomas Hertz office Number: ‪980-999-2613‬‬
      </FooterText>
    </PageWrapper>
  );
};

export default ContactUsPage; 